// VCR + rare glitch post-effect: tracking noise, chroma bleed, and occasional
// glitch bursts (horizontal tears, RGB split, block corruption). Deterministic
// from sim time; no wall-clock.

import * as THREE from 'three';

export const VcrGlitchShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    time: { value: 0 },
    resolution: { value: new THREE.Vector2(1, 1) },
    strength: { value: 0.5 },
    glitchStrength: { value: 0.8 },
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform vec2 resolution;
    uniform float strength;
    uniform float glitchStrength;
    varying vec2 vUv;

    float hash(float n) {
      return fract(sin(n * 127.1) * 43758.5453);
    }

    float hash2(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
    }

    void main() {
      vec2 uv = vUv;

      // VCR tracking band: slow vertical sweep with horizontal tear + noise.
      // Tear shift and grain kept small so the band never scallops the
      // playfield border or reads as a mid-screen grain stripe.
      float band = fract(uv.y - time * 0.03);
      float tracking = smoothstep(0.0, 0.15, band) * smoothstep(0.3, 0.15, band);
      uv.x += tracking * (hash2(vec2(floor(time * 30.0), 0.0)) - 0.5) * 0.006 * strength;
      float trackingNoise = tracking * (hash2(uv * resolution * 0.5 + time) - 0.5) * 0.15 * strength;

      // Rare glitch burst: every ~12 s, 30% chance, ~0.6 s duration.
      float cycle = floor(time / 12.0);
      float phase = fract(time / 12.0);
      float glitchActive = step(0.95, phase) * step(hash(cycle), 0.3);
      float glitchIntensity = glitchActive * (1.0 - (phase - 0.95) / 0.05) * glitchStrength;

      // Horizontal displacement (wave tear): lower amplitude and frequency
      // than the original so the border wobbles gently instead of scalloping.
      float tear = sin(uv.y * 44.0 + time * 22.0) * 0.0055 * glitchIntensity;
      uv.x += tear;

      // RGB split.
      vec2 ca = vec2(0.002 * glitchIntensity, 0.0);
      vec3 col;
      col.r = texture2D(tDiffuse, uv + ca).r;
      col.g = texture2D(tDiffuse, uv).g;
      col.b = texture2D(tDiffuse, uv - ca).b;

      // Block corruption: quantize and occasionally drop blocks. Drops only
      // darken (never pure black) and stay out of the central 60% ellipse
      // so the playfield is never obscured by dead-pixel blocks.
      vec2 block = floor(uv * resolution / 8.0);
      float drop = step(0.999, hash2(block + floor(time * 20.0))) * glitchIntensity;
      vec2 centerUv = (uv - 0.5) / 0.3;
      drop *= step(1.0, dot(centerUv, centerUv));
      col = mix(col, vec3(0.0), min(drop, 0.3));

      // Apply tracking noise and slight chroma bleed.
      col += trackingNoise;
      col.r += tracking * 0.03 * strength;
      col.b -= tracking * 0.03 * strength;

      gl_FragColor = vec4(col, 1.0);
    }
  `,
};
